// ════════════════════════════════════════════════════════════════════════════
// M2.7 Madurez de Procesos — panel "Análisis V·IA Governance" (réplica de M1.5)
// Captura cualitativa (conclusión del consultor, read-only aquí) + generación IA
// on-demand + diagnóstico narrativo + DAFO + recomendaciones priorizadas +
// hoja de ruta + fortalezas/riesgos + validación human-in-the-loop.
// Consume /process/madurez/* (api-madurez-proceso). Estructura paritaria con
// MadurezAnalisisIA de M1.5 usando el kit duplicado analisis-ui-proceso.
// i18n vía useI18n (process.madurez.analisis), con fallback ES inline en s().
// ════════════════════════════════════════════════════════════════════════════
import { useState, useEffect, useCallback } from 'react';
import { useI18n } from '../../../../../i18n';
import {
  fetchAnalisisVigenteBpmm,
  generarAnalisisBpmm,
  validarAnalisisBpmm,
  descargarInformeProcesoWord,
  descargarExportadorPyProceso,
  descargarMegapromptProceso,
  obtenerInformeHtmlProceso,
  type AnalisisBpmm,
  type FuenteAnalisisBpmm,
  type PilarBpmm,
} from '../api-madurez-proceso';
import { MadurezProcesoDiscrepancias } from './MadurezProcesoDiscrepancias';

import {
  COLORS,
  SectionCard,
  Badge,
  Markdown,
  ListaPunteada,
  DAFOMatrix,
  type DafoData,
} from './analisis-ui-proceso';

const FUENTES: FuenteAnalisisBpmm[] = ['CUESTIONARIO', 'REAL', 'AMBAS'];

const PILAR_LABEL: Record<PilarBpmm, string> = {
  ESTRATEGIA: 'Estrategia',
  PROCESOS: 'Procesos',
  METODO: 'Método',
  TECNOLOGIA: 'Tecnología',
  PERSONAS: 'Personas',
  GOBIERNO: 'Gobierno',
  CULTURA: 'Cultura',
};

/** Responsable y horizonte orientativos por pilar (réplica del mapeo DIM_RESP de M1.5). */
const PILAR_RESP: Record<PilarBpmm, { responsable: string; horizonte: string }> = {
  ESTRATEGIA: { responsable: 'Dirección / Comité de procesos', horizonte: '0–3 meses' },
  GOBIERNO: { responsable: 'Oficina de procesos (BPM CoE)', horizonte: '0–3 meses' },
  PROCESOS: { responsable: 'Oficina de procesos', horizonte: '1–4 meses' },
  METODO: { responsable: 'Oficina de procesos / Calidad', horizonte: '3–6 meses' },
  TECNOLOGIA: { responsable: 'TI / Sistemas', horizonte: '3–6 meses' },
  PERSONAS: { responsable: 'RR.HH.', horizonte: '3–6 meses' },
  CULTURA: { responsable: 'Dirección / RR.HH.', horizonte: '6–12 meses' },
};

const A1 = '#1F3864';
const A2 = '#2F5496';
const CYAN = '#33BCE1';
const GO = '#595959';
const GM = '#D9D9D9';
const W = '#FFF';
const VERDE = '#16A34A';
const ROJO = '#DC2626';

const estadoColor: Record<string, string> = { BORRADOR: GO, GENERADO: A2, VALIDADO: VERDE };

/** Primer párrafo de texto (no encabezado/viñeta) del diagnóstico → resumen ejecutivo. */
function primerParrafo(md: string): string {
  for (const linea of md.split('\n')) {
    const l = linea.trim();
    if (!l || l.startsWith('#') || l.startsWith('-') || l.startsWith('*') || l.startsWith('>'))
      continue;
    return l.replace(/\*\*/g, '');
  }
  return md.slice(0, 280).replace(/\*\*/g, '');
}

/** DAFO derivada de lo que el análisis ya produce cuando la IA no la generó (como M1.5). */
function derivarDafo(analisis: AnalisisBpmm): DafoData {
  const pilaresDebiles = [...new Set(analisis.recomendaciones.map((r) => r.pilar))];
  return {
    fortalezas: analisis.fortalezas ?? [],
    amenazas: analisis.riesgos ?? [],
    debilidades: pilaresDebiles.map((p) => `Pilar a reforzar: ${PILAR_LABEL[p]}`),
    oportunidades: [...analisis.recomendaciones]
      .sort((a, b) => a.prioridad - b.prioridad)
      .slice(0, 5)
      .map((r) => r.textoFinal),
  };
}

interface Props {
  evaluacionId: string;
  estadoEvaluacion: string;
  /** Conclusión del ingeniero (solo lectura aquí; la edición vive en el dashboard). */
  observacionesIniciales: string | null;
  /** Cambia al guardar el cuestionario → fuerza refresco de las discrepancias. */
  discrepKey?: number;
  onChanged?: () => void;
}

export function MadurezProcesoAnalisisIA({
  evaluacionId,
  estadoEvaluacion,
  observacionesIniciales,
  discrepKey,
  onChanged,
}: Props) {
  const { t } = useI18n();
  const s = useCallback(
    (k: string, fb: string): string => {
      const mk = (((t.process as Record<string, unknown>)?.madurez as Record<string, unknown>)
        ?.analisis ?? {}) as Record<string, unknown>;
      return typeof mk[k] === 'string' ? (mk[k] as string) : fb;
    },
    [t],
  );

  const [analisis, setAnalisis] = useState<AnalisisBpmm | null>(null);
  // Conclusión del ingeniero: solo LECTURA aquí (la edición vive en el dashboard).
  const observaciones = observacionesIniciales ?? '';
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [validating, setValidating] = useState(false);
  // Descarga en curso: 'word' | 'py' | 'mp' | 'html' | null.
  const [exporting, setExporting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Fuente de la lectura que analiza la IA: cuestionario, real (mapa) o ambas.
  const [fuente, setFuente] = useState<FuenteAnalisisBpmm>('AMBAS');
  const busy = generating || validating || exporting !== null;
  const congelada = estadoEvaluacion === 'APROBADA';

  useEffect(() => {
    let activo = true;
    setLoading(true);
    setError(null);
    fetchAnalisisVigenteBpmm(evaluacionId)
      .then((res) => {
        if (activo) {
          setAnalisis(res.data);
          setLoading(false);
        }
      })
      .catch((err: Error) => {
        if (activo) {
          setError(err.message);
          setLoading(false);
        }
      });
    return () => {
      activo = false;
    };
  }, [evaluacionId]);

  const generar = useCallback(async () => {
    setGenerating(true);
    setError(null);
    try {
      const { data } = await generarAnalisisBpmm(evaluacionId, fuente);
      setAnalisis(data);
      onChanged?.();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setGenerating(false);
    }
  }, [evaluacionId, fuente, onChanged]);

  const validar = useCallback(async () => {
    if (!analisis) return;
    setValidating(true);
    setError(null);
    try {
      const { data } = await validarAnalisisBpmm(analisis.id);
      setAnalisis(data);
      onChanged?.();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setValidating(false);
    }
  }, [analisis, onChanged]);

  // ── Descargas (Parte B, M7-M13): el filename real lo fija el backend ──
  const descargarInforme = useCallback(async () => {
    if (!analisis) return;
    setExporting('word');
    setError(null);
    try {
      await descargarInformeProcesoWord(analisis.id);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setExporting(null);
    }
  }, [analisis]);

  const exportarPy = useCallback(async () => {
    if (!analisis) return;
    setExporting('py');
    setError(null);
    try {
      await descargarExportadorPyProceso(analisis.id);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setExporting(null);
    }
  }, [analisis]);

  const exportarMegaprompt = useCallback(async () => {
    setExporting('mp');
    setError(null);
    try {
      await descargarMegapromptProceso(evaluacionId);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setExporting(null);
    }
  }, [evaluacionId]);

  const verInformeHtml = useCallback(async () => {
    setExporting('html');
    setError(null);
    // Abrir la pestaña SÍNCRONAMENTE en el gesto del clic evita el bloqueo de popups.
    const win = window.open('', '_blank');
    try {
      const url = await obtenerInformeHtmlProceso(evaluacionId);
      if (win) win.location.href = url;
      else window.open(url, '_blank');
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      if (win) win.close();
      setError((err as Error).message);
    } finally {
      setExporting(null);
    }
  }, [evaluacionId]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        fontFamily: "'DM Sans',sans-serif",
      }}
    >
      {/* ══ Análisis V·IA Governance (cabecera con controles + descargas + resultados) ══ */}
      <SectionCard
        title={s('analisisIA', 'Análisis V·IA Governance')}
        accent={COLORS.a1}
        right={
          analisis?.estado === 'VALIDADO' ? (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <button onClick={descargarInforme} disabled={busy} style={btnMini(A1, W, busy)}>
                {exporting === 'word'
                  ? s('generandoInforme', 'Generando…')
                  : s('descargarInforme', 'Descargar informe Word')}
              </button>
              <button onClick={exportarPy} disabled={busy} style={btnMini(A2, W, busy)}>
                {exporting === 'py'
                  ? s('generandoInforme', 'Generando…')
                  : s('descargarPy', 'Descargar exportador .py')}
              </button>
              <button
                onClick={exportarMegaprompt}
                disabled={busy}
                style={btnMini(CYAN, '#0B2545', busy)}
              >
                {exporting === 'mp'
                  ? s('generandoInforme', 'Generando…')
                  : s('generarMegaprompt', 'Generar megaprompt claude.ai')}
              </button>
              <button onClick={verInformeHtml} disabled={busy} style={btnMini(VERDE, W, busy)}>
                {exporting === 'html'
                  ? s('generandoInforme', 'Generando…')
                  : s('verInformeHtml', 'Generar informe HTML')}
              </button>
            </div>
          ) : undefined
        }
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            fontFamily: "'DM Sans',sans-serif",
          }}
        >
          {error && (
            <div
              style={{
                background: '#FDECEC',
                border: `1px solid ${ROJO}`,
                color: '#7B0000',
                borderRadius: 6,
                padding: '8px 12px',
                fontSize: 12,
              }}
            >
              {error}
            </div>
          )}

          {/* ── Acción generar ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            {/* Fuente: qué lectura analiza la IA */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, color: GO, fontWeight: 600 }}>
                {s('fuenteAnalisis', 'Analizar')}:
              </span>
              <div style={{ display: 'flex', gap: 4 }}>
                {FUENTES.map((f) => {
                  const sel = fuente === f;
                  const fb =
                    f === 'CUESTIONARIO' ? 'Cuestionario' : f === 'REAL' ? 'Real' : 'Ambas';
                  return (
                    <button
                      key={f}
                      type="button"
                      onClick={() => setFuente(f)}
                      disabled={generating}
                      title={s(
                        `fuenteHelp_${f}`,
                        f === 'CUESTIONARIO'
                          ? 'Percepción del equipo / criterio del ingeniero'
                          : f === 'REAL'
                            ? 'Lectura objetiva del mapa de procesos'
                            : 'Compara real vs percepción y resalta la brecha',
                      )}
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        padding: '4px 10px',
                        borderRadius: 6,
                        border: sel ? `1px solid ${A1}` : `1px solid ${GM}`,
                        background: sel ? A1 : W,
                        color: sel ? W : GO,
                        cursor: generating ? 'not-allowed' : 'pointer',
                        fontFamily: "'DM Sans',sans-serif",
                      }}
                    >
                      {s(`fuente_${f}`, fb)}
                    </button>
                  );
                })}
              </div>
            </div>
            <button onClick={generar} disabled={busy} style={btnStyle(A1, W, busy)}>
              {generating
                ? s('generando', 'Generando análisis…')
                : analisis
                  ? s('regenerar', 'Regenerar análisis IA')
                  : s('generarAnalisis', 'Generar análisis IA')}
            </button>
            {analisis && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  padding: '3px 10px',
                  borderRadius: 12,
                  background: '#EEF',
                  color: estadoColor[analisis.estado] ?? GO,
                }}
              >
                {analisis.estado}
              </span>
            )}
            {analisis?.fuente && (
              <span style={{ fontSize: 11, color: GO }}>
                {s('fuenteAnalisis', 'Analizar')}:{' '}
                <strong style={{ color: A2 }}>
                  {s(
                    `fuente_${analisis.fuente}`,
                    analisis.fuente === 'CUESTIONARIO'
                      ? 'Cuestionario'
                      : analisis.fuente === 'REAL'
                        ? 'Real'
                        : 'Ambas',
                  )}
                </strong>
              </span>
            )}
            {analisis?.proveedor && (
              <span style={{ fontSize: 11, color: GO }}>
                {s('via', 'V·IA')}: {analisis.proveedor}
                {analisis.modeloUsado ? ` · ${analisis.modeloUsado}` : ''}
              </span>
            )}
          </div>

          <div style={{ fontSize: 10, color: GO, fontStyle: 'italic' }}>
            {s('basadoNotas', 'El análisis incorpora las notas de gobernanza del consultor.')}
          </div>

          {loading ? (
            <div style={{ color: GO, fontSize: 12, fontStyle: 'italic' }}>
              {s('cargando', 'Cargando…')}
            </div>
          ) : !analisis ? (
            <div style={{ color: GO, fontSize: 12, fontStyle: 'italic' }}>
              {s(
                'sinAnalisis',
                'Aún no hay análisis IA. Genera uno: la IA diagnostica la madurez de procesos y propone acciones de mejora priorizadas (la IA propone, el consultor valida).',
              )}
            </div>
          ) : (
            <>
              {/* ── Resumen ejecutivo ── */}
              <SectionCard
                title={s('resumenEjecutivo', 'Resumen ejecutivo')}
                accent={COLORS.cyan}
                right={
                  analisis.estado === 'VALIDADO' ? (
                    <Badge tono="verde">{s('validado', 'Validado')}</Badge>
                  ) : (
                    <Badge tono="azul">{s('borradorIa', 'Borrador IA')}</Badge>
                  )
                }
              >
                {observaciones.trim() && (
                  <p
                    style={{
                      fontSize: 13,
                      color: COLORS.ink,
                      lineHeight: 1.55,
                      margin: '0 0 8px',
                      borderLeft: `3px solid ${COLORS.cyan}`,
                      paddingLeft: 10,
                    }}
                  >
                    <strong style={{ color: COLORS.a1 }}>
                      {s('lecturaConsultor', 'Lectura del consultor')}:{' '}
                    </strong>
                    {observaciones.trim()}
                  </p>
                )}
                <p style={{ fontSize: 13, color: COLORS.ink, lineHeight: 1.55, margin: 0 }}>
                  {analisis.conclusionEjecutiva || primerParrafo(analisis.diagnosticoNarrativo)}
                </p>
                {analisis.recomendaciones.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                    {[...analisis.recomendaciones]
                      .sort((a, b) => a.prioridad - b.prioridad)
                      .slice(0, 3)
                      .map((r, i) => (
                        <span
                          key={i}
                          style={{
                            fontSize: 11,
                            padding: '3px 9px',
                            borderRadius: 12,
                            background: COLORS.gc,
                            color: COLORS.a1,
                            fontWeight: 600,
                          }}
                        >
                          #{r.prioridad} {PILAR_LABEL[r.pilar]}
                        </span>
                      ))}
                  </div>
                )}
              </SectionCard>

              {/* ── Matriz DAFO (IA si está, si no derivada) ── */}
              <SectionCard title={s('dafo', 'Matriz DAFO')} accent={COLORS.a2}>
                <DAFOMatrix data={analisis.matrizDafo ?? derivarDafo(analisis)} />
              </SectionCard>

              {/* ── Diagnóstico ── */}
              <SectionCard title={s('diagnostico', 'Diagnóstico')} accent={COLORS.a1}>
                <Markdown text={analisis.diagnosticoNarrativo} />
              </SectionCard>

              {/* ── Recomendaciones ── */}
              {analisis.recomendaciones.length > 0 && (
                <SectionCard
                  title={s('recomendaciones', 'Recomendaciones')}
                  accent={COLORS.verde}
                  hint={String(analisis.recomendaciones.length)}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[...analisis.recomendaciones]
                      .sort((a, b) => a.prioridad - b.prioridad)
                      .map((r, i) => (
                        <div
                          key={i}
                          style={{
                            border: `1px solid ${COLORS.gm}`,
                            borderLeft: `3px solid ${COLORS.a2}`,
                            borderRadius: 8,
                            padding: '10px 12px',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              marginBottom: 4,
                              flexWrap: 'wrap',
                            }}
                          >
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 800,
                                color: COLORS.w,
                                background: COLORS.a1,
                                borderRadius: 10,
                                padding: '1px 8px',
                              }}
                            >
                              #{r.prioridad}
                            </span>
                            <span style={{ fontSize: 11, fontWeight: 800, color: COLORS.a2 }}>
                              {PILAR_LABEL[r.pilar]}
                            </span>
                            <Badge tono="verde">{s('anclada', 'anclada al catálogo')}</Badge>
                          </div>
                          <div style={{ fontSize: 13, color: COLORS.ink, lineHeight: 1.5 }}>
                            {r.textoFinal}
                          </div>
                          {r.justificacion && (
                            <div
                              style={{
                                fontSize: 11,
                                color: COLORS.go,
                                marginTop: 4,
                                fontStyle: 'italic',
                                lineHeight: 1.45,
                              }}
                            >
                              {r.justificacion}
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                </SectionCard>
              )}

              {/* ── Fortalezas / Riesgos ── */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <SectionCard title={s('fortalezas', 'Fortalezas')} accent={COLORS.verde}>
                  <ListaPunteada color={COLORS.verde} items={analisis.fortalezas} />
                </SectionCard>
                <SectionCard title={s('riesgos', 'Riesgos')} accent={COLORS.rojo}>
                  <ListaPunteada color={COLORS.rojo} items={analisis.riesgos} />
                </SectionCard>
              </div>

              {/* ── Discrepancias realidad ↔ cuestionario (multi-respondedor) ── */}
              <SectionCard
                title={s('discrepancias', 'Discrepancias del cuestionario')}
                accent={COLORS.a2}
              >
                <MadurezProcesoDiscrepancias
                  key={discrepKey}
                  evaluacionId={evaluacionId}
                  labels={{
                    empty: s('discrepanciasEmpty', 'Sin respuestas registradas todavía.'),
                    pregunta: s('colPregunta', 'Pregunta'),
                    respondentes: s('colRespondentes', 'Respond.'),
                    varianza: s('colVarianza', 'Varianza'),
                    discrepante: s('discrepante', 'Discrepancia (varianza ≥ umbral)'),
                  }}
                />
              </SectionCard>

              {/* ── Hoja de ruta (recomendaciones priorizadas + responsable/horizonte) · ÚLTIMA ── */}
              {analisis.recomendaciones.length > 0 && (
                <SectionCard
                  title={s('hojaRuta', 'Hoja de ruta')}
                  accent={COLORS.cyan}
                  hint={String(analisis.recomendaciones.length)}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[...analisis.recomendaciones]
                      .sort((a, b) => a.prioridad - b.prioridad)
                      .map((r, i) => {
                        const resp = PILAR_RESP[r.pilar];
                        return (
                          <div
                            key={i}
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 10,
                              border: `1px solid ${COLORS.gm}`,
                              borderLeft: `3px solid ${COLORS.cyan}`,
                              borderRadius: 8,
                              padding: '10px 12px',
                            }}
                          >
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 800,
                                color: COLORS.w,
                                background: COLORS.a1,
                                borderRadius: 10,
                                padding: '1px 8px',
                                flexShrink: 0,
                                marginTop: 1,
                              }}
                            >
                              #{r.prioridad}
                            </span>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  marginBottom: 3,
                                  flexWrap: 'wrap',
                                }}
                              >
                                <span style={{ fontSize: 11, fontWeight: 800, color: COLORS.a2 }}>
                                  {PILAR_LABEL[r.pilar]}
                                </span>
                              </div>
                              <div style={{ fontSize: 13, color: COLORS.ink, lineHeight: 1.5 }}>
                                {r.textoFinal}
                              </div>
                              {resp && (
                                <div
                                  style={{
                                    fontSize: 11,
                                    color: COLORS.go,
                                    marginTop: 5,
                                    display: 'flex',
                                    gap: 12,
                                    flexWrap: 'wrap',
                                  }}
                                >
                                  <span>
                                    <strong style={{ color: COLORS.a1 }}>
                                      {s('responsable', 'Responsable')}:
                                    </strong>{' '}
                                    {resp.responsable}
                                  </span>
                                  <span>
                                    <strong style={{ color: COLORS.a1 }}>
                                      {s('horizonte', 'Horizonte')}:
                                    </strong>{' '}
                                    {resp.horizonte}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </SectionCard>
              )}

              {/* ── Validar ── */}
              {analisis.estado === 'GENERADO' && !congelada && (
                <div>
                  <button onClick={validar} disabled={busy} style={btnStyle(VERDE, W, busy)}>
                    {s('validar', 'Validar análisis')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </SectionCard>
    </div>
  );
}

function btnStyle(bg: string, color: string, disabled: boolean): React.CSSProperties {
  return {
    fontSize: 12,
    fontWeight: 700,
    padding: '6px 12px',
    borderRadius: 6,
    border: 'none',
    background: bg,
    color,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    fontFamily: "'DM Sans',sans-serif",
  };
}

/** Botón compacto para la cabecera del card (descargas). */
function btnMini(bg: string, color: string, disabled: boolean): React.CSSProperties {
  return {
    fontSize: 11,
    fontWeight: 700,
    padding: '3px 9px',
    borderRadius: 6,
    border: 'none',
    background: bg,
    color,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    fontFamily: "'DM Sans',sans-serif",
  };
}
